import { useState, useCallback, useRef, useEffect } from 'react';
import { geocodeCity, GeocodingResult, idbKeys } from '../../pipeline/overpass';
import { theme } from '../../styles/theme';
import { CityBounds } from '../../types/map';

interface CitySearchProps {
  onCitySelected: (result: GeocodingResult) => void;
  onBack?: () => void;
}

/**
 * Save city metadata to localStorage (tiny — just name/coords, fits in 5MB easily).
 * The actual map data goes in IndexedDB.
 */
export function saveCachedCityMetadata(result: GeocodingResult, bounds: CityBounds) {
  const bbox = `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`;
  try {
    const saved = localStorage.getItem('geo-sleeper-city-meta') || '{}';
    const meta = JSON.parse(saved);
    meta[bbox] = { name: result.name, displayName: result.displayName, lat: result.lat, lng: result.lng };
    localStorage.setItem('geo-sleeper-city-meta', JSON.stringify(meta));
    console.log(`[CityCache] Saved metadata for ${result.name}`);
  } catch { /* ignore */ }
}

/**
 * Scan IndexedDB for cached map data keys and match with localStorage metadata.
 */
async function getCachedCities(): Promise<GeocodingResult[]> {
  const cities: GeocodingResult[] = [];
  const cachePrefix = 'cache-';

  try {
    const keys = await idbKeys();
    console.log(`[CityCache] IndexedDB keys:`, keys);

    // Load name metadata from localStorage (tiny JSON)
    let meta: Record<string, { name: string; displayName: string; lat: number; lng: number }> = {};
    try {
      meta = JSON.parse(localStorage.getItem('geo-sleeper-city-meta') || '{}');
    } catch { /* ignore */ }

    for (const key of keys) {
      if (!key.startsWith(cachePrefix)) continue;

      const bbox = key.slice(cachePrefix.length);
      const parts = bbox.split(',').map(Number);
      if (parts.length !== 4 || parts.some(isNaN)) continue;

      const [south, west, north, east] = parts;
      const bounds: CityBounds = { south, west, north, east };
      const lat = (south + north) / 2;
      const lng = (west + east) / 2;

      const cityMeta = meta[bbox];
      const name = cityMeta?.name || `${lat.toFixed(2)}°, ${lng.toFixed(2)}°`;
      const displayName = cityMeta?.displayName || name;

      cities.push({
        name: cityMeta?.name || name,
        displayName,
        lat: cityMeta?.lat || lat,
        lng: cityMeta?.lng || lng,
        bounds,
      });
    }
  } catch (e) {
    console.warn(`[CityCache] Error scanning IndexedDB:`, e);
  }

  // Dedupe by name — if multiple cache entries share a display name (e.g. repeated
  // searches producing slightly different bboxes), keep only the first.
  const seen = new Set<string>();
  const unique: GeocodingResult[] = [];
  for (const c of cities) {
    const key = c.name.toLowerCase().trim();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(c);
    }
  }

  console.log(`[CityCache] Found ${unique.length} unique cached cities:`, unique.map(c => c.name));
  return unique;
}

export function CitySearch({ onCitySelected, onBack }: CitySearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number>(0);
  const [cachedCities, setCachedCities] = useState<GeocodingResult[]>([]);

  useEffect(() => {
    getCachedCities().then(setCachedCities);
  }, []);

  const handleSearch = useCallback(async (value: string) => {
    setQuery(value);
    setError(null);

    if (value.length < 2) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setSearching(true);
      try {
        const geocoded = await geocodeCity(value);
        setResults(geocoded);
      } catch (err) {
        setError('Search failed. Try again.');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Title */}
        <div style={styles.titleBlock}>
          <div style={styles.classification}>TOP SECRET // NOFORN</div>
          <h1 style={styles.title}>GEO-SLEEPER</h1>
          <div style={styles.subtitle}>OPERATIONAL THEATER SELECTION</div>
        </div>

        {/* Search */}
        <div style={styles.searchBlock}>
          <label style={styles.label}>ENTER CITY NAME</label>
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Prague, Tokyo, Chicago..."
            style={styles.input}
            autoFocus
          />
          {searching && <div style={styles.searching}>SEARCHING...</div>}
          {error && <div style={styles.error}>{error}</div>}
        </div>

        {/* Quick-select cached cities */}
        {cachedCities.length > 0 && (
          <div style={styles.presetsBlock}>
            <div style={styles.presetsLabel}>CACHED CITIES</div>
            <div style={styles.presetsRow}>
              {cachedCities.map((city) => (
                <button
                  key={city.name}
                  style={styles.presetBtn}
                  onClick={() => onCitySelected(city)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.accentGreen;
                    e.currentTarget.style.color = theme.colors.accentGreen;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.border;
                    e.currentTarget.style.color = theme.colors.textPrimary;
                  }}
                >
                  {city.name.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div style={styles.results}>
            {results.map((result, i) => (
              <button
                key={i}
                style={styles.resultItem}
                onClick={() => onCitySelected(result)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.colors.surfaceLight;
                  e.currentTarget.style.borderColor = theme.colors.accentGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.colors.surface;
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                <div style={styles.resultName}>{result.name}</div>
                <div style={styles.resultDetail}>{result.displayName}</div>
                <div style={styles.resultCoords}>
                  {result.lat.toFixed(4)}°N, {result.lng.toFixed(4)}°E
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Back button + Footer */}
        {onBack && (
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <button
              style={{
                padding: '8px 24px',
                background: 'rgba(100, 100, 100, 0.15)',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '4px',
                color: theme.colors.textSecondary,
                fontFamily: theme.fonts.mono,
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '2px',
                cursor: 'pointer',
              }}
              onClick={onBack}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#44aaff'; e.currentTarget.style.color = '#44aaff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.color = theme.colors.textSecondary; }}
            >
              ← BACK TO MENU
            </button>
          </div>
        )}
        <div style={styles.footer}>
          <div style={styles.footerLine}>SELECT OPERATIONAL CITY TO BEGIN MISSION BRIEFING</div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.colors.background,
  },
  content: {
    width: '100%',
    maxWidth: '560px',
    padding: '40px',
  },
  titleBlock: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  classification: {
    fontSize: '11px',
    letterSpacing: '3px',
    color: theme.colors.accentRed,
    marginBottom: '16px',
    fontWeight: 500,
  },
  title: {
    fontSize: '48px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    letterSpacing: '8px',
    marginBottom: '12px',
    fontFamily: theme.fonts.display,
  },
  subtitle: {
    fontSize: '12px',
    letterSpacing: '4px',
    color: theme.colors.textSecondary,
    fontWeight: 400,
  },
  searchBlock: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '2px',
    color: theme.colors.textPrimary,
    fontSize: '16px',
    fontFamily: theme.fonts.mono,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  searching: {
    fontSize: '11px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
    marginTop: '8px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  error: {
    fontSize: '11px',
    color: theme.colors.accentRed,
    marginTop: '8px',
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '32px',
  },
  resultItem: {
    width: '100%',
    padding: '14px 16px',
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '2px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: theme.fonts.mono,
    transition: 'all 0.15s',
    color: theme.colors.textPrimary,
  },
  resultName: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.colors.accentGreen,
    marginBottom: '4px',
  },
  resultDetail: {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    marginBottom: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resultCoords: {
    fontSize: '10px',
    color: theme.colors.textDim,
    letterSpacing: '1px',
  },
  presetsBlock: {
    marginBottom: '24px',
  },
  presetsLabel: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    marginBottom: '8px',
  },
  presetsRow: {
    display: 'flex',
    gap: '8px',
  },
  presetBtn: {
    flex: 1,
    padding: '10px 12px',
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '2px',
    color: theme.colors.textPrimary,
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: theme.fonts.mono,
    letterSpacing: '2px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  footer: {
    textAlign: 'center',
    marginTop: '32px',
  },
  footerLine: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
  },
};
